import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';
import SpecializedMap from './SpecializedMap';
import { getArticleBySlug, getArticleBySlugAdmin } from './services/articleService';
import { getAllMedia, checkIsAdmin } from './services/mediaService';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

// Helper to convert Firestore timestamp or string to Date
const getDateValue = (date) => {
  if (!date) return new Date(0);
  if (date.toDate) return date.toDate(); // Firestore Timestamp
  if (date.seconds) return new Date(date.seconds * 1000); // Firestore Timestamp object
  return new Date(date); // String date
};

// Helper to format date for display
const formatDate = (date) => {
  const d = getDateValue(date);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Article state
  const [article, setArticle] = useState(null);
  const [articleLoading, setArticleLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check admin status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const adminStatus = await checkIsAdmin(user.uid);
        setIsAdmin(adminStatus);
      } else {
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch article from Firestore
  useEffect(() => {
    const fetchArticle = async () => {
      setArticleLoading(true);
      try {
        // Try public fetch first (works for everyone with published articles)
        let fetchedArticle = await getArticleBySlug(slug);

        // If not found and user is admin, try admin fetch (includes drafts)
        if (!fetchedArticle && isAdmin) {
          fetchedArticle = await getArticleBySlugAdmin(slug);
        }

        setArticle(fetchedArticle);
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        setArticleLoading(false);
      }
    };
    fetchArticle();
  }, [slug, isAdmin]);

  // Use Firestore article
  const currentArticle = article;

  const [golfCourses, setGolfCourses] = useState([]);
  const isMobile = window.innerWidth <= 768;
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [visibleCourses, setVisibleCourses] = useState([]);
  // Memoize sidebarCourses to prevent unnecessary re-renders
  const memoizedSidebarCourses = useMemo(() => visibleCourses, [visibleCourses]);

  // Add this for premium logic (for now, always true)
  const isPremiumUser = true;

  // Filter state (copied from main map, but static for teaser)
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
  const [prioritiesDropdownOpen, setPrioritiesDropdownOpen] = useState(false);
  const [architectDropdownOpen, setArchitectDropdownOpen] = useState(false);
  const [mediaDropdownOpen, setMediaDropdownOpen] = useState(false);

  // Use the same options as the main map
  const allTypes = [
    "Public",
    "Municipal",
    "Semi-Private",
    "Resort",
    "University",
    "Military",
    "Private",
    "18 Holes"
  ];
  const priorities = [
    { value: 'distance', label: 'Distance from Map Center' },
    { value: 'desc', label: 'Price: High to Low' },
    { value: 'asc', label: 'Price: Low to High' }
  ];
  // Flexible filter state initialization from mapConfig
  const allCourseTypes = Array.from(new Set(golfCourses.map(c => c.type).filter(Boolean)));
  const [minPrice, setMinPrice] = useState(currentArticle?.mapConfig?.minPrice ?? null);
  const [maxPrice, setMaxPrice] = useState(currentArticle?.mapConfig?.maxPrice ?? null);
  const [selectedTypes, setSelectedTypes] = useState(currentArticle?.mapConfig?.types ?? allCourseTypes);
  const [sortOrder, setSortOrder] = useState(currentArticle?.mapConfig?.sortOrder ?? "distance");
  const [rankedOnly, setRankedOnly] = useState(currentArticle?.mapConfig?.rankedOnly ?? false);
  const [selectedArchitects, setSelectedArchitects] = useState(currentArticle?.mapConfig?.architect ? [currentArticle.mapConfig.architect] : []);
  // Support customCriteria in mapConfig (including selectedCourseIds for dynamic course selection)
  const [customCriteria, setCustomCriteria] = useState(currentArticle?.mapConfig?.customCriteria ?? (currentArticle?.mapConfig?.selectedCourseIds ? { selectedCourseIds: currentArticle.mapConfig.selectedCourseIds } : {}));
  // Build architect options directly from course data
  const allArchitectOptions = Array.from(
    new Set(golfCourses.map(c => c.course_architect).filter(Boolean))
  );

  // Only set initial architect selection once per article
  const hasSetInitialArchitects = React.useRef(false);

  // Add architectSearch state
  const [architectSearch, setArchitectSearch] = useState("");
  const showSelectedArchitects = architectSearch === "__SHOW_SELECTED__";

  // Set default types for course type filter
  const defaultTypes = [
    "Public",
    "Municipal",
    "Semi-Private",
    "Resort",
    "University",
    "Military",
    "Private"
  ];
  const isDefaultTypes = selectedTypes.length === defaultTypes.length && defaultTypes.every(t => selectedTypes.includes(t));
  const isDefaultRanked = rankedOnly === false;
  const isCourseTypeDefault = isDefaultTypes && isDefaultRanked;

  // 1. Add state for media filter
  const [hasMediaOnly, setHasMediaOnly] = useState(false);
  const [selectedMediaTypes, setSelectedMediaTypes] = useState([]);
  const [mediaList, setMediaList] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  
  // 2. Add state for Top Courses filters
  const [top100AllOnly, setTop100AllOnly] = useState(false);
  const [top100PublicOnly, setTop100PublicOnly] = useState(false);

  // 2. Load media from Firestore
  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const firestoreMedia = await getAllMedia();
        if (firestoreMedia && firestoreMedia.length > 0) {
          setMediaList(firestoreMedia);
        }
      } catch (error) {
        console.error('Error fetching media:', error);
      }
    };
    fetchMedia();
  }, []);

  const featuredMediaTypes = ['Design Disasters', 'Fried Egg Golf'];

  // Fetch golf courses data
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/courses.json`)
      .then((res) => res.json())
      .then((data) => {
        setGolfCourses(data);
      });
  }, []);

  useEffect(() => {
    if (golfCourses.length > 0 && selectedTypes.length === 0) {
      setSelectedTypes(allCourseTypes);
    }
  }, [golfCourses]);

  // When golfCourses are loaded, if selectedArchitects is empty and not overridden by the article, set to allArchitectOptions
  useEffect(() => {
    if (
      !articleLoading &&
      golfCourses.length > 0 &&
      selectedArchitects.length === 0 &&
      !hasSetInitialArchitects.current &&
      !(currentArticle && currentArticle.hasMap && (currentArticle.mapConfig?.architect || currentArticle.mapConfig?.customCriteria || currentArticle.mapConfig?.selectedCourseIds))
    ) {
      setSelectedArchitects(allArchitectOptions);
    }
  }, [golfCourses, currentArticle, articleLoading]);

  // When the article is loaded and has a mapConfig.architect, select all matching architect names (robust for MacKenzie, substring for others)
  useEffect(() => {
    if (
      !hasSetInitialArchitects.current &&
      currentArticle &&
      currentArticle.hasMap &&
      currentArticle.mapConfig &&
      currentArticle.mapConfig.architect &&
      golfCourses.length > 0
    ) {
      let matchingArchitects;
      if (currentArticle.mapConfig.architect.toLowerCase().includes('mackenzie')) {
        // Robust normalization for MacKenzie
        matchingArchitects = allArchitectOptions.filter(a => {
          if (!a) return false;
          const normalized = a.toLowerCase().replace(/allister|alister/g, 'mackenzie').replace(/\s+/g, ' ').trim();
          return normalized.includes('mackenzie');
        });
      } else {
        // Substring match for other architects
        const target = currentArticle.mapConfig.architect.toLowerCase();
        matchingArchitects = allArchitectOptions.filter(a => a && a.toLowerCase().includes(target));
      }
      setSelectedArchitects(matchingArchitects);
      hasSetInitialArchitects.current = true;
    }
  }, [currentArticle, golfCourses, allArchitectOptions]);

  // When article loads with selectedCourseIds, update customCriteria and clear architect filter
  const hasSetCustomCriteria = React.useRef(false);
  useEffect(() => {
    if (
      !hasSetCustomCriteria.current &&
      currentArticle &&
      currentArticle.hasMap &&
      currentArticle.mapConfig?.selectedCourseIds?.length > 0
    ) {
      setCustomCriteria({ selectedCourseIds: currentArticle.mapConfig.selectedCourseIds });
      setSelectedArchitects([]); // Clear architect filter when using hand-picked courses
      hasSetCustomCriteria.current = true;
      hasSetInitialArchitects.current = true; // Prevent architect useEffect from overriding
    }
  }, [currentArticle]);

  // Close filter dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        !e.target.closest('.filter-group') &&
        !e.target.closest('.dropdown-caret') &&
        !e.target.closest('.dropdown-close')
      ) {
        setDropdownOpen(false);
        setMoreDropdownOpen(false);
        setPrioritiesDropdownOpen(false);
        setArchitectDropdownOpen(false);
        setMediaDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent infinite loop: only update visibleCourses if changed
  const lastVisibleCoursesRef = useRef([]);
  const handleVisibleCoursesChange = (courses) => {
    const prev = lastVisibleCoursesRef.current;
    const isSame =
      prev.length === courses.length &&
      prev.every((c, i) => c.course_id === courses[i].course_id);
    if (!isSame) {
      // console.log('[ArticleDetail] setVisibleCourses called with', courses.length, 'courses');
      setVisibleCourses(courses);
      lastVisibleCoursesRef.current = courses;
    }
  };

  // Compose a summary of applied filters
  const filterSummary = [
    selectedArchitects.length > 0 ? `Architect: ${selectedArchitects.join(', ')}` : null,
    selectedTypes.length > 0 ? `Type: ${selectedTypes.join(', ')}` : null,
    minPrice !== null ? `Min Price: $${minPrice}` : null,
    maxPrice !== null ? `Max Price: $${maxPrice}` : null,
    rankedOnly ? 'Ranked Only' : null,
    sortOrder !== 'distance' ? `Sort: ${sortOrder}` : null,
    top100AllOnly ? 'Top 100 All Only' : null,
    top100PublicOnly ? 'Top 100 Public Only' : null,
    customCriteria && Object.keys(customCriteria).length > 0 ? `Custom: ${JSON.stringify(customCriteria)}` : null
  ].filter(Boolean).join(' | ');

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Show loading state
  if (articleLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f7f7' }}>
        <div style={{ padding: '2rem', color: '#666' }}>Loading article...</div>
      </div>
    );
  }

  if (!currentArticle) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f7f7' }}>
        <div style={{ padding: '2rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
          <h2>Article Not Found</h2>
          <p>The requested article could not be found.</p>
          <button
            onClick={() => navigate('/')}
            className="btn btn-primary"
            style={{ marginTop: '1rem' }}
          >
            Back to Articles
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f7f7f7' }}>
      <Helmet>
        <title>{currentArticle.title}</title>
        <meta name="description" content={currentArticle.excerpt} />
      </Helmet>

      {/* Header */}
      <div style={{
        background: '#fff',
        padding: isMobile ? '1rem' : '2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h1 style={{ margin: 0, fontSize: isMobile ? '1.5rem' : '2rem', color: '#333' }}>{currentArticle.title}</h1>
          <div style={{ display: 'flex', gap: '8px' }}>
            {isMobile && currentArticle.hasMap && (
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="btn btn-secondary"
              >
                Filters
              </button>
            )}
            <button
              onClick={() => navigate('/')}
              className="btn btn-primary"
            >
              Back to Articles
            </button>
          </div>
        </div>
        {/* Desktop Filter Bar */}
        {!isMobile && currentArticle.hasMap && (
          <div style={{
            display: 'flex',
            gap: 14,
            marginTop: 24,
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-start',
            maxWidth: 1200,
            marginLeft: 'auto',
            marginRight: 'auto',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: '12px 18px',
          }}>
            {/* Price Filter Dropdown */}
            <div className="filter-group desktop-only">
              <div
                className={`price-dropdown-toggle ${dropdownOpen || minPrice !== null || maxPrice !== null ? "active" : ""}`}
                onClick={() => {
                  setDropdownOpen((prev) => !prev);
                  setMoreDropdownOpen(false);
                  setPrioritiesDropdownOpen(false);
                  setArchitectDropdownOpen(false);
                  setMediaDropdownOpen(false);
                }}
                style={{
                  minWidth: 170,
                  borderRadius: 10,
                  padding: '8px 18px',
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#222',
                  background: dropdownOpen || minPrice !== null || maxPrice !== null ? '#e4f8ec' : '#fff',
                  border: dropdownOpen || minPrice !== null || maxPrice !== null ? '1.5px solid #1D665D' : '1px solid #ccc',
                  boxSizing: 'border-box',
                  transition: 'all 0.15s'
                }}
              >
                <span>
                  {minPrice !== null && maxPrice !== null
                    ? `$${minPrice} – $${maxPrice}`
                    : minPrice !== null
                    ? `$${minPrice}+`
                    : maxPrice !== null
                    ? `$0 – $${maxPrice}`
                    : "Price"}
                </span>
                <svg
                  className={`dropdown-caret ${dropdownOpen ? "open" : ""}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                >
                  <path d="M7 10l5 5 5-5z" fill="currentColor" />
                </svg>
              </div>
              {dropdownOpen && (
                <div className="price-dropdown-menu">
                  <button className="dropdown-close" onClick={() => setDropdownOpen(false)} aria-label="Close">×</button>
                  <label htmlFor="min-price-select">Min Price</label>
                  <select
                    id="min-price-select"
                    value={minPrice === null ? '' : minPrice}
                    onChange={e => setMinPrice(e.target.value === '' ? null : Number(e.target.value))}
                  >
                    <option value="">No Min</option>
                    <option value="0">$0</option>
                    <option value="10">$10</option>
                    <option value="20">$20</option>
                    <option value="30">$30</option>
                    <option value="40">$40</option>
                    <option value="50">$50</option>
                    <option value="60">$60</option>
                    <option value="70">$70</option>
                    <option value="80">$80</option>
                    <option value="90">$90</option>
                    <option value="100">$100</option>
                    <option value="125">$125</option>
                    <option value="150">$150</option>
                    <option value="175">$175</option>
                    <option value="200">$200</option>
                    <option value="250">$250</option>
                    <option value="300">$300+</option>
                  </select>
                  <label htmlFor="max-price-select">Max Price</label>
                  <select
                    id="max-price-select"
                    value={maxPrice === null ? '' : maxPrice}
                    onChange={e => setMaxPrice(e.target.value === '' ? null : Number(e.target.value))}
                  >
                    <option value="">No Max</option>
                    <option value="10">$10</option>
                    <option value="20">$20</option>
                    <option value="30">$30</option>
                    <option value="40">$40</option>
                    <option value="50">$50</option>
                    <option value="60">$60</option>
                    <option value="70">$70</option>
                    <option value="80">$80</option>
                    <option value="90">$90</option>
                    <option value="100">$100</option>
                    <option value="125">$125</option>
                    <option value="150">$150</option>
                    <option value="175">$175</option>
                    <option value="200">$200</option>
                    <option value="250">$250</option>
                    <option value="300">$300+</option>
                  </select>
                  <button className="reset-button" onClick={() => { setMinPrice(null); setMaxPrice(null); }}>Reset</button>
                  <button className="apply-button" onClick={() => { setDropdownOpen(false); }}>Apply</button>
                </div>
              )}
            </div>
            {/* Course Type Filter Dropdown */}
            <div className="filter-group desktop-only">
              <div
                className={`more-dropdown-toggle ${moreDropdownOpen || !isCourseTypeDefault ? "active" : ""}`}
                style={{
                  minWidth: 170,
                  borderRadius: 10,
                  padding: '8px 18px',
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#222',
                  background: !isCourseTypeDefault ? '#e4f8ec' : '#fff',
                  border: !isCourseTypeDefault ? '1.5px solid #1D665D' : '1px solid #ccc',
                  boxSizing: 'border-box',
                  transition: 'all 0.15s'
                }}
                onClick={() => {
                  setMoreDropdownOpen((prev) => !prev);
                  setDropdownOpen(false);
                  setPrioritiesDropdownOpen(false);
                  setArchitectDropdownOpen(false);
                  setMediaDropdownOpen(false);
                }}
              >
                <span>Course Type</span>
                <svg
                  className={`dropdown-caret ${moreDropdownOpen ? "open" : ""}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                >
                  <path d="M7 10l5 5 5-5z" fill="currentColor" />
                </svg>
              </div>
              {moreDropdownOpen && (
                <div className="more-dropdown-menu">
                  <button className="dropdown-close" onClick={() => setMoreDropdownOpen(false)} aria-label="Close">×</button>
                  <div className="more-dropdown-title">Course Types</div>
                  <div className="select-buttons">
                    <button type="button" onClick={() => setSelectedTypes(allTypes)}>Select All</button>
                    <button type="button" onClick={() => setSelectedTypes([])}>Deselect All</button>
                  </div>
                  <div className="more-option">
                    {allTypes.map((type) => (
                      <div key={type}>
                        <input
                          type="checkbox"
                          id={`type-${type}`}
                          checked={selectedTypes.includes(type)}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedTypes(prev => [...prev, type]);
                            } else {
                              setSelectedTypes(prev => prev.filter(t => t !== type));
                            }
                          }}
                        />
                        <label htmlFor={`type-${type}`}>{type}</label>
                      </div>
                    ))}
                  </div>
                  <div className="more-dropdown-divider"></div>
                  <div className="more-option">
                    <div>
                      <input
                        type="checkbox"
                        id="ranked-only"
                        checked={rankedOnly}
                        onChange={e => setRankedOnly(e.target.checked)}
                      />
                      <label htmlFor="ranked-only">Ranked Courses Only</label>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Architect Filter Dropdown */}
            <div className="filter-group desktop-only">
              <div
                className={`more-dropdown-toggle ${architectDropdownOpen || selectedArchitects.some(a => a && a.trim().length > 0) ? "active" : ""}`}
                style={{
                  minWidth: 170,
                  borderRadius: 10,
                  padding: '8px 18px',
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#222',
                  background: architectDropdownOpen || selectedArchitects.some(a => a && a.trim().length > 0) ? '#e4f8ec' : '#fff',
                  border: architectDropdownOpen || selectedArchitects.some(a => a && a.trim().length > 0) ? '1.5px solid #1D665D' : '1px solid #ccc',
                  boxSizing: 'border-box',
                  transition: 'all 0.15s'
                }}
                onClick={() => {
                  const next = !architectDropdownOpen;
                  setArchitectDropdownOpen(next);
                  if (next) setDropdownOpen(false);
                  setMoreDropdownOpen(false);
                  setPrioritiesDropdownOpen(false);
                  setMediaDropdownOpen(false);
                }}
              >
                <span>Architect{selectedArchitects.filter(a => a && a.trim().length > 0).length > 0 ? ` (${selectedArchitects.filter(a => a && a.trim().length > 0).length})` : ""}</span>
                <svg
                  className={`dropdown-caret ${architectDropdownOpen ? "open" : ""}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                >
                  <path d="M7 10l5 5 5-5z" fill="currentColor" />
                </svg>
              </div>
              {architectDropdownOpen && (
                <div className="more-dropdown-menu" style={{ maxHeight: 300, overflowY: 'auto', minWidth: 220, zIndex: 1000 }}>
                  <button className="dropdown-close" onClick={() => setArchitectDropdownOpen(false)} aria-label="Close">×</button>
                  <div style={{ position: 'relative', marginBottom: 10, width: '100%' }}>
                    <input
                      type="text"
                      placeholder="Search architects..."
                      value={showSelectedArchitects ? "" : architectSearch}
                      onChange={e => setArchitectSearch(e.target.value)}
                      style={{ width: '82%', padding: '6px 32px 6px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14 }}
                      disabled={showSelectedArchitects}
                    />
                    {architectSearch && architectSearch !== "__SHOW_SELECTED__" && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setArchitectSearch("");
                        }}
                        style={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: '#e0e0e0',
                          border: 'none',
                          borderRadius: '50%',
                          width: 22,
                          height: 22,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 16,
                          cursor: 'pointer',
                          color: '#888',
                          lineHeight: 1,
                          padding: 0
                        }}
                        tabIndex={-1}
                        aria-label="Clear search"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setArchitectSearch(architectSearch === "__SHOW_SELECTED__" ? "" : "__SHOW_SELECTED__")}
                    style={{
                      width: '100%',
                      padding: '6px 12px',
                      marginBottom: 10,
                      background: architectSearch === "__SHOW_SELECTED__" ? '#e4f8ec' : '#f0f0f0',
                      border: '1px solid #ccc',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 14
                    }}
                  >
                    {architectSearch === "__SHOW_SELECTED__" ? "Show All" : "View Selected"}
                  </button>
                  <div className="select-buttons" style={{ marginBottom: 10 }}>
                    <button type="button" onClick={() => setSelectedArchitects(allArchitectOptions)}>Select All</button>
                    <button type="button" onClick={() => setSelectedArchitects([])}>Deselect All</button>
                  </div>
                  <div className="more-dropdown-title">Architects</div>
                  <div className="more-option">
                    {(showSelectedArchitects
                      ? allArchitectOptions.filter(arch => selectedArchitects.includes(arch))
                      : allArchitectOptions.filter(arch => arch.toLowerCase().includes(architectSearch.toLowerCase()))
                    ).map((arch) => (
                      <div key={arch}>
                        <input
                          type="checkbox"
                          id={`arch-${arch}`}
                          checked={selectedArchitects.includes(arch)}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedArchitects(prev => [...prev, arch]);
                            } else {
                              setSelectedArchitects(prev => prev.filter(a => a !== arch));
                            }
                          }}
                        />
                        <label htmlFor={`arch-${arch}`}>{arch}</label>
                      </div>
                    ))}
                  </div>
                  <div className="more-dropdown-divider"></div>
                </div>
              )}
            </div>
            {/* Media Filter Dropdown */}
            <div className="filter-group desktop-only">
              <div
                className={`more-dropdown-toggle ${mediaDropdownOpen || hasMediaOnly || selectedMediaTypes.length > 0 || top100AllOnly || top100PublicOnly ? "active" : ""}`}
                style={{
                  minWidth: 170,
                  borderRadius: 10,
                  padding: '8px 18px',
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#222',
                  background: (hasMediaOnly || selectedMediaTypes.length > 0 || top100AllOnly || top100PublicOnly) ? '#e4f8ec' : '#fff',
                  border: (hasMediaOnly || selectedMediaTypes.length > 0 || top100AllOnly || top100PublicOnly) ? '1.5px solid #1D665D' : '1px solid #ccc',
                  boxSizing: 'border-box',
                  transition: 'all 0.15s'
                }}
                onClick={() => {
                  setMediaDropdownOpen((prev) => !prev);
                  setDropdownOpen(false);
                  setMoreDropdownOpen(false);
                  setPrioritiesDropdownOpen(false);
                  setArchitectDropdownOpen(false);
                }}
              >
                <span>Other</span>
                <svg
                  className={`dropdown-caret ${mediaDropdownOpen ? "open" : ""}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                >
                  <path d="M7 10l5 5 5-5z" fill="currentColor" />
                </svg>
              </div>
              {mediaDropdownOpen && (
                <div className="more-dropdown-menu">
                  <button className="dropdown-close" onClick={() => setMediaDropdownOpen(false)} aria-label="Close">×</button>
                  <div className="more-dropdown-title">Media Types</div>
                  <div className="select-buttons">
                    <button type="button" onClick={() => {
                      setSelectedMediaTypes(featuredMediaTypes);
                      setTop100AllOnly(true);
                      setTop100PublicOnly(true);
                    }}>Select All</button>
                    <button type="button" onClick={() => {
                      setSelectedMediaTypes([]);
                      setTop100AllOnly(false);
                      setTop100PublicOnly(false);
                    }}>Deselect All</button>
                  </div>
                  <div className="more-option">
                    <div>
                      <input
                        type="checkbox"
                        id="has-media"
                        checked={hasMediaOnly}
                        onChange={e => setHasMediaOnly(e.target.checked)}
                      />
                      <label htmlFor="has-media">Courses with Media</label>
                    </div>
                  </div>
                  <div className="more-dropdown-divider"></div>
                  <div style={{ fontWeight: 600, margin: '8px 0 4px 0', fontSize: 14 }}>Featured Media</div>
                  <div className="more-option">
                    {featuredMediaTypes.map((type) => (
                      <div key={type}>
                        <input
                          type="checkbox"
                          id={`media-${type}`}
                          checked={selectedMediaTypes.includes(type)}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedMediaTypes(prev => [...prev, type]);
                            } else {
                              setSelectedMediaTypes(prev => prev.filter(t => t !== type));
                            }
                          }}
                        />
                        <label htmlFor={`media-${type}`}>{type}</label>
                      </div>
                    ))}
                  </div>
                  <div className="more-dropdown-divider"></div>
                  <div style={{ fontWeight: 600, margin: '8px 0 4px 0', fontSize: 14 }}>Top Courses</div>
                  <div className="more-option">
                    <div>
                      <input
                        type="checkbox"
                        id="top100-all"
                        checked={top100AllOnly}
                        onChange={e => setTop100AllOnly(e.target.checked)}
                      />
                      <label htmlFor="top100-all">Golf Digest Top 100 Courses in America</label>
                    </div>
                    <div>
                      <input
                        type="checkbox"
                        id="top100-public"
                        checked={top100PublicOnly}
                        onChange={e => setTop100PublicOnly(e.target.checked)}
                      />
                      <label htmlFor="top100-public">Golf Digest Top 100 Public Courses in America</label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Filters Modal */}
      {isMobile && currentArticle.hasMap && isMobileMenuOpen && (
        <div className="mobile-filters-modal-overlay" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="mobile-filters-modal" onClick={e => e.stopPropagation()}>
            <button className="mobile-filters-close" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close filters">×</button>
            <div className="mobile-filters-content" style={{ gap: 10 }}>
              {/* Price Filter */}
              <div className="filter-group mobile-visible">
                <div
                  className={`price-dropdown-toggle ${dropdownOpen || minPrice !== null || maxPrice !== null ? "active" : ""}`}
                  onClick={() => {
                    setDropdownOpen((prev) => !prev);
                    setMoreDropdownOpen(false);
                    setPrioritiesDropdownOpen(false);
                    setArchitectDropdownOpen(false);
                    setMediaDropdownOpen(false);
                  }}
                >
                  <span>
                    {minPrice !== null && maxPrice !== null
                      ? `$${minPrice} – $${maxPrice}`
                      : minPrice !== null
                      ? `$${minPrice}+`
                      : maxPrice !== null
                      ? `$0 – $${maxPrice}`
                      : "Price"}
                  </span>
                  <svg
                    className={`dropdown-caret ${dropdownOpen ? "open" : ""}`}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                  >
                    <path d="M7 10l5 5 5-5z" fill="currentColor" />
                  </svg>
                </div>
                {dropdownOpen && (
                  <div className="price-dropdown-menu">
                    <button className="dropdown-close" onClick={() => setDropdownOpen(false)} aria-label="Close">×</button>
                    <label htmlFor="min-price-select-mobile">Min Price</label>
                    <select id="min-price-select-mobile" value={minPrice === null ? '' : minPrice} onChange={e => setMinPrice(e.target.value === '' ? null : Number(e.target.value))}>
                      <option value="">No Min</option>
                      <option value="0">$0</option>
                      <option value="10">$10</option>
                      <option value="20">$20</option>
                      <option value="30">$30</option>
                      <option value="40">$40</option>
                      <option value="50">$50</option>
                      <option value="60">$60</option>
                      <option value="70">$70</option>
                      <option value="80">$80</option>
                      <option value="90">$90</option>
                      <option value="100">$100</option>
                      <option value="125">$125</option>
                      <option value="150">$150</option>
                      <option value="175">$175</option>
                      <option value="200">$200</option>
                      <option value="250">$250</option>
                      <option value="300">$300+</option>
                    </select>
                    <label htmlFor="max-price-select-mobile">Max Price</label>
                    <select id="max-price-select-mobile" value={maxPrice === null ? '' : maxPrice} onChange={e => setMaxPrice(e.target.value === '' ? null : Number(e.target.value))}>
                      <option value="">No Max</option>
                      <option value="10">$10</option>
                      <option value="20">$20</option>
                      <option value="30">$30</option>
                      <option value="40">$40</option>
                      <option value="50">$50</option>
                      <option value="60">$60</option>
                      <option value="70">$70</option>
                      <option value="80">$80</option>
                      <option value="90">$90</option>
                      <option value="100">$100</option>
                      <option value="125">$125</option>
                      <option value="150">$150</option>
                      <option value="175">$175</option>
                      <option value="200">$200</option>
                      <option value="250">$250</option>
                      <option value="300">$300+</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Course Type Filter */}
              <div className="filter-group mobile-visible">
                <div
                  className={`more-dropdown-toggle ${moreDropdownOpen || !isCourseTypeDefault ? "active" : ""}`}
                  onClick={() => {
                    setMoreDropdownOpen((prev) => !prev);
                    setDropdownOpen(false);
                    setPrioritiesDropdownOpen(false);
                    setArchitectDropdownOpen(false);
                    setMediaDropdownOpen(false);
                  }}
                >
                  <span>Course Type</span>
                  <svg
                    className={`dropdown-caret ${moreDropdownOpen ? "open" : ""}`}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                  >
                    <path d="M7 10l5 5 5-5z" fill="currentColor" />
                  </svg>
                </div>
                {moreDropdownOpen && (
                  <div className="more-dropdown-menu">
                    <button className="dropdown-close" onClick={() => setMoreDropdownOpen(false)} aria-label="Close">×</button>
                    <div className="more-dropdown-title">Course Types</div>
                    <div className="select-buttons">
                      <button
                        type="button"
                        onClick={() => setSelectedTypes(allTypes)}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedTypes([])}
                      >
                        Deselect All
                      </button>
                    </div>
                    <div className="more-option">
                      {allTypes.map((type) => (
                        <div key={type}>
                          <input
                            type="checkbox"
                            id={`type-mobile-${type}`}
                            checked={selectedTypes.includes(type)}
                            onChange={e => {
                              if (e.target.checked) {
                                setSelectedTypes(prev => [...prev, type]);
                              } else {
                                setSelectedTypes(prev => prev.filter(t => t !== type));
                              }
                            }}
                          />
                          <label htmlFor={`type-mobile-${type}`}>{type}</label>
                        </div>
                      ))}
                    </div>
                    <div className="more-dropdown-divider"></div>
                    <div className="more-option">
                      <div>
                        <input
                          type="checkbox"
                          id="ranked-only-mobile"
                          checked={rankedOnly}
                          onChange={e => setRankedOnly(e.target.checked)}
                        />
                        <label htmlFor="ranked-only-mobile">Ranked Courses Only</label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Architect Filter */}
              <div className="filter-group mobile-visible">
                <div
                  className={`more-dropdown-toggle ${architectDropdownOpen || selectedArchitects.some(a => a && a.trim().length > 0) ? "active" : ""}`}
                  onClick={() => {
                    setArchitectDropdownOpen((prev) => !prev);
                    setDropdownOpen(false);
                    setMoreDropdownOpen(false);
                    setPrioritiesDropdownOpen(false);
                    setMediaDropdownOpen(false);
                  }}
                >
                  <span>Architect{selectedArchitects.filter(a => a && a.trim().length > 0).length > 0 ? ` (${selectedArchitects.filter(a => a && a.trim().length > 0).length})` : ""}</span>
                  <svg
                    className={`dropdown-caret ${architectDropdownOpen ? "open" : ""}`}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                  >
                    <path d="M7 10l5 5 5-5z" fill="currentColor" />
                  </svg>
                </div>
                {architectDropdownOpen && (
                  <div className="more-dropdown-menu" style={{ maxHeight: 300, overflowY: 'auto' }}>
                    <button className="dropdown-close" onClick={() => setArchitectDropdownOpen(false)} aria-label="Close">×</button>
                    <div className="more-dropdown-title">Architects</div>
                    <div className="more-option">
                      {allArchitectOptions.map((arch) => (
                        <div key={arch}>
                          <input
                            type="checkbox"
                            id={`arch-mobile-${arch}`}
                            checked={selectedArchitects.includes(arch)}
                            onChange={e => {
                              if (e.target.checked) {
                                setSelectedArchitects(prev => [...prev, arch]);
                              } else {
                                setSelectedArchitects(prev => prev.filter(a => a !== arch));
                              }
                            }}
                          />
                          <label htmlFor={`arch-mobile-${arch}`}>{arch}</label>
                        </div>
                      ))}
                    </div>
                    <div className="more-dropdown-divider"></div>
                    <div className="more-option">
                      <button
                        type="button"
                        onClick={() => setSelectedArchitects(allArchitectOptions)}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedArchitects([])}
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Media Filter */}
              <div className="filter-group mobile-visible">
                <div
                  className={`more-dropdown-toggle ${mediaDropdownOpen || hasMediaOnly || selectedMediaTypes.length > 0 || top100AllOnly || top100PublicOnly ? "active" : ""}`}
                  style={{
                    minWidth: 170,
                    borderRadius: 10,
                    padding: '8px 18px',
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#222',
                    background: (hasMediaOnly || selectedMediaTypes.length > 0 || top100AllOnly || top100PublicOnly) ? '#e4f8ec' : '#fff',
                    border: (hasMediaOnly || selectedMediaTypes.length > 0 || top100AllOnly || top100PublicOnly) ? '1.5px solid #1D665D' : '1px solid #ccc',
                    boxSizing: 'border-box',
                    transition: 'all 0.15s'
                  }}
                  onClick={() => {
                    setMediaDropdownOpen((prev) => !prev);
                    setDropdownOpen(false);
                    setMoreDropdownOpen(false);
                    setPrioritiesDropdownOpen(false);
                    setArchitectDropdownOpen(false);
                  }}
                >
                  <span>Other</span>
                  <svg
                    className={`dropdown-caret ${mediaDropdownOpen ? "open" : ""}`}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                  >
                    <path d="M7 10l5 5 5-5z" fill="currentColor" />
                  </svg>
                </div>
                {mediaDropdownOpen && (
                  <div className="more-dropdown-menu">
                    <button className="dropdown-close" onClick={() => setMediaDropdownOpen(false)} aria-label="Close">×</button>
                    <div className="more-dropdown-title">Media Types</div>
                    <div className="select-buttons">
                      <button type="button" onClick={() => {
                        setSelectedMediaTypes(featuredMediaTypes);
                        setTop100AllOnly(true);
                        setTop100PublicOnly(true);
                      }}>Select All</button>
                      <button type="button" onClick={() => {
                        setSelectedMediaTypes([]);
                        setTop100AllOnly(false);
                        setTop100PublicOnly(false);
                      }}>Deselect All</button>
                    </div>
                    <div className="more-option">
                      <div>
                        <input
                          type="checkbox"
                          id="has-media-mobile"
                          checked={hasMediaOnly}
                          onChange={e => setHasMediaOnly(e.target.checked)}
                        />
                        <label htmlFor="has-media-mobile">Courses with Media</label>
                      </div>
                    </div>
                    <div className="more-dropdown-divider"></div>
                    <div style={{ fontWeight: 600, margin: '8px 0 4px 0', fontSize: 14 }}>Featured Media</div>
                    <div className="more-option">
                      {featuredMediaTypes.map((type) => (
                        <div key={type}>
                          <input
                            type="checkbox"
                            id={`media-mobile-${type}`}
                            checked={selectedMediaTypes.includes(type)}
                            onChange={e => {
                              if (e.target.checked) {
                                setSelectedMediaTypes(prev => [...prev, type]);
                              } else {
                                setSelectedMediaTypes(prev => prev.filter(t => t !== type));
                              }
                            }}
                          />
                          <label htmlFor={`media-mobile-${type}`}>{type}</label>
                        </div>
                      ))}
                    </div>
                    <div className="more-dropdown-divider"></div>
                    <div style={{ fontWeight: 600, margin: '8px 0 4px 0', fontSize: 14 }}>Top Courses</div>
                    <div className="more-option">
                      <div>
                        <input
                          type="checkbox"
                          id="top100-all-mobile"
                          checked={top100AllOnly}
                          onChange={e => setTop100AllOnly(e.target.checked)}
                        />
                        <label htmlFor="top100-all-mobile">Golf Digest Top 100 Courses in America</label>
                      </div>
                      <div>
                        <input
                          type="checkbox"
                          id="top100-public-mobile"
                          checked={top100PublicOnly}
                          onChange={e => setTop100PublicOnly(e.target.checked)}
                        />
                        <label htmlFor="top100-public-mobile">Golf Digest Top 100 Public Courses in America</label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Article Content */}
      <div style={{
        maxWidth: 1200,
        margin: '2rem auto',
        padding: isMobile ? '0 1rem' : '0 2rem',
        paddingBottom: '4rem'
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {currentArticle.hasMap ? (
            <div style={{ height: isMobile ? 400 : 600 }}>
              <SpecializedMap
                golfCourses={golfCourses}
                selectedCourse={selectedCourse}
                setSelectedCourse={setSelectedCourse}
                showSidebar={!isMobile}
                sidebarCourses={memoizedSidebarCourses}
                onVisibleCoursesChange={handleVisibleCoursesChange}
                selectedTypes={selectedTypes}
                selectedArchitects={selectedArchitects}
                minPrice={minPrice}
                maxPrice={maxPrice}
                rankedOnly={rankedOnly}
                sortOrder={sortOrder}
                mediaList={mediaList}
                hasMediaOnly={hasMediaOnly}
                selectedMediaTypes={selectedMediaTypes}
                customCriteria={customCriteria}
                top100AllOnly={top100AllOnly}
                top100PublicOnly={top100PublicOnly}
              />
            </div>
          ) : (
            <div style={{
              maxWidth: 600,
              margin: '0 auto',
              padding: '1rem'
            }}>
              <img
                src={currentArticle.image}
                alt={currentArticle.title}
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 8,
                  display: 'block'
                }}
              />
            </div>
          )}
          <div style={{ padding: '2rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              fontSize: 14,
              color: '#666'
            }}>
              <span>{formatDate(currentArticle.date)}</span>
              <span>{currentArticle.readTime}</span>
            </div>
            <div style={{
              fontSize: 16,
              lineHeight: 1.8,
              color: '#333'
            }}>
              {/* Render HTML content for the article (sanitized to prevent XSS) */}
              <div className="article-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentArticle.content) }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ArticleDetail; 